import React, { useEffect, useMemo, useState } from "react";
import { useManifest } from "./useManifest.js";
import type { Filters, ManifestAsset } from "./types.js";
import { assetMatchesPath } from "./folderTree.js";
import { readFolderFromUrl, writeFolderToUrl, readLockedFolder } from "./folderDeepLink.js";
import { AssetGrid } from "./components/AssetGrid.js";
import { FilterPanel } from "./components/FilterPanel.js";
import { FolderTree } from "./components/FolderTree.js";
import { Breadcrumb } from "./components/Breadcrumb.js";
import { DownloadModal } from "./components/DownloadModal.js";
import { BulkDownloadModal } from "./components/BulkDownloadModal.js";
import { SelectionBar } from "./components/SelectionBar.js";
import { Header } from "./components/Header.js";
import { useSelection } from "./useSelection.js";
import { useAdminMode } from "./admin/useAdminMode.js";
import { useAdminQueue } from "./admin/useAdminQueue.js";
import { useAuth } from "./admin/useAuth.js";
import { AdminBar } from "./components/admin/AdminBar.js";
import { QueuePanel } from "./components/admin/QueuePanel.js";
import { CommitModal } from "./components/admin/CommitModal.js";
import { AddAssetModal } from "./components/admin/AddAssetModal.js";
import { EditAssetModal } from "./components/admin/EditAssetModal.js";
import { MagicLinkModal } from "./components/admin/MagicLinkModal.js";
import type { MagicLinkScope } from "./admin/magicLink.js";
import { findVariantsForAsset, moveAssetToInternal, fetchBlocklist, uploadBlocklist } from "./admin/graph.js";
import { useSession } from "./admin/useSession.js";

/**
 * The apex domain is an auth wall (the "Canto model", 2026-07-02): everyone
 * must sign in with their Moldex account AND hold the DamAdmin role before the
 * library loads. The gate also waits for the session-cookie exchange — the
 * manifest and /cdn images are refused server-side without the cookie, so
 * loading the library any earlier would just 401.
 */
export function App() {
  const auth = useAuth();
  // The SERVER decides access (POST /auth/session validates the token + role
  // and logs every denial). We call it for any signed-in account and let its
  // verdict drive the gate — the client role claim is only a UI hint inside.
  const session = useSession(!!auth.account);

  if (auth.loading) return <div className="loading">Checking sign-in…</div>;
  if (!auth.account) return <SignInScreen onSignIn={() => void auth.signIn()} error={auth.error} />;
  if (session.state === "denied") {
    // Deliberately generic — no mention of roles, groups, or how to get access.
    return (
      <div className="error">
        <h1>Access denied</h1>
        <p>This account isn&rsquo;t authorized to use the asset library.</p>
        <button className="signin-button" onClick={() => void auth.signOut()}>Sign out</button>
      </div>
    );
  }
  if (session.state === "error") {
    return (
      <div className="error">
        <h1>Couldn&rsquo;t start your session</h1>
        <p>Please try again in a moment.</p>
        <button className="signin-button" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }
  if (session.state !== "ready") return <div className="loading">Preparing your session…</div>;
  return <AuthedApp auth={auth} />;
}

function SignInScreen({ onSignIn, error }: { onSignIn: () => void; error: string | null }) {
  return (
    <div className="signin-screen">
      <img src="/moldex-logo.png" alt="Moldex" className="signin-logo" />
      <h1>Moldex Asset Library</h1>
      <p>Sign in with your Moldex account to browse the library.</p>
      <button className="signin-button" onClick={onSignIn}>Sign in with Microsoft</button>
      {error && <p className="signin-error">{error}</p>}
    </div>
  );
}

function AuthedApp({ auth }: { auth: ReturnType<typeof useAuth> }) {
  const { manifest, loading, error, blockedIds, refetchBlocklist, addBlocked, addOverlayAsset } =
    useManifest();
  const adminMode = useAdminMode();
  const { queued, toggle, remove, clear, addMany: queueMany, queueRange } = useAdminQueue();
  const selection = useSelection();
  const [showBulkDownload, setShowBulkDownload] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [editingAsset, setEditingAsset] = useState<ManifestAsset | null>(null);
  // Locked-folder mode (?only=<albumPath>): confine the whole UI to one folder.
  const lockedFolder = useMemo(() => readLockedFolder(), []);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    // ?only= wins; otherwise ?folder= just lands (still navigable).
    folderPath: lockedFolder || readFolderFromUrl(),
    regions: new Set<string>(),
    productCategories: new Set<string>(),
    assetTypes: new Set<string>(),
    schemes: new Set<string>(),
  });
  const [selected, setSelected] = useState<ManifestAsset | null>(null);
  const [showCommit, setShowCommit] = useState(false);
  const [magicLinkScope, setMagicLinkScope] = useState<MagicLinkScope | null>(null);

  // Folder setter that, in locked mode, refuses to navigate above the locked root.
  const setFolder = (folderPath: string) => {
    const clamped =
      lockedFolder && !(folderPath === lockedFolder || folderPath.startsWith(`${lockedFolder}/`))
        ? lockedFolder
        : folderPath;
    setFilters((f) => ({ ...f, folderPath: clamped }));
  };

  // Reflect the folder into the URL for sharing — but not in locked mode, where
  // the canonical link is the ?only= URL itself.
  useEffect(() => {
    if (!lockedFolder) writeFolderToUrl(filters.folderPath);
  }, [filters.folderPath, lockedFolder]);

  // Apply the blocklist to manifest.assets so anything that's been admin-removed
  // doesn't render. Admins still see the queue panel for items they've added
  // but not yet committed.
  const visibleAssets = useMemo(() => {
    if (!manifest) return [];
    let assets = manifest.assets;
    // Locked mode: nothing outside the locked folder is ever visible (grid,
    // search, filters and folder tree all derive from this set).
    if (lockedFolder) assets = assets.filter((a) => assetMatchesPath(a, lockedFolder));
    if (blockedIds.size) assets = assets.filter((a) => !blockedIds.has(a.id));
    return assets;
  }, [manifest, blockedIds, lockedFolder]);

  const filtered = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    return visibleAssets.filter((a) => {
      if (!assetMatchesPath(a, filters.folderPath)) return false;
      if (s && !a.searchBlob.includes(s)) return false;
      if (filters.regions.size && (!a.region || !filters.regions.has(a.region))) return false;
      if (
        filters.productCategories.size &&
        (!a.productCategory || !filters.productCategories.has(a.productCategory))
      ) {
        return false;
      }
      if (filters.assetTypes.size && (!a.assetType || !filters.assetTypes.has(a.assetType))) return false;
      if (filters.schemes.size && !filters.schemes.has(a.scheme)) return false;
      return true;
    });
  }, [visibleAssets, filters]);

  const assetsById = useMemo(() => {
    const m = new Map<string, ManifestAsset>();
    if (manifest) for (const a of manifest.assets) m.set(a.id, a);
    return m;
  }, [manifest]);

  // Assets backing the current download selection. Resolved from the full map
  // (not just `filtered`) so a selection survives filtering/navigation.
  const selectedAssets = useMemo(
    () =>
      [...selection.selected]
        .map((id) => assetsById.get(id))
        .filter((a): a is ManifestAsset => a != null),
    [selection.selected, assetsById],
  );

  // Existing album paths, for the "Add asset" folder datalist.
  const albumPaths = useMemo(() => {
    if (!manifest) return [];
    const set = new Set<string>();
    for (const a of manifest.assets) if (a.albumPath) set.add(a.albumPath);
    return [...set].sort();
  }, [manifest]);

  if (loading) return <div className="loading">Loading asset library…</div>;
  if (error) {
    return (
      <div className="error">
        <h1>Couldn&rsquo;t load the asset library</h1>
        <p>{error}</p>
      </div>
    );
  }
  if (!manifest) return null;

  const queuedAssetsForCommit: ManifestAsset[] = [...queued]
    .map((id) => assetsById.get(id))
    .filter((a): a is ManifestAsset => a != null);

  // Admin actions only render when the URL flag is on AND the user is signed
  // in with the DamAdmin app role. The bar itself still renders without the
  // role so unauthorized users can see why nothing happens.
  const adminActive = adminMode && !!auth.account && auth.hasAdminRole;

  // Magic links only need the sign-in + role, NOT the admin URL flag — a
  // curator browsing normally can mint a link from any entry point (folder
  // right-click, single file, or the selection bar).
  const canMagicLink = !!auth.account && auth.hasAdminRole;

  return (
    <div className={`app ${adminMode ? "admin-mode" : ""}`}>
      {adminMode && (
        <AdminBar
          account={auth.account}
          loading={auth.loading}
          error={auth.error}
          hasAdminRole={auth.hasAdminRole}
          roles={auth.roles}
          onSignIn={() => void auth.signIn()}
          onSignOut={() => void auth.signOut()}
          onAddAsset={adminActive ? () => setShowAddAsset(true) : undefined}
        />
      )}
      <Header manifest={manifest} filters={filters} setFilters={setFilters} resultCount={filtered.length} />
      <div className="main">
        <div className="left-rail">
          <FolderTree
            assets={visibleAssets}
            selectedPath={filters.folderPath}
            onSelect={setFolder}
            rootPath={lockedFolder}
            onMagicLink={
              canMagicLink ? (path) => setMagicLinkScope({ kind: "folder", folder: path }) : undefined
            }
          />
          <FilterPanel manifest={manifest} filters={filters} setFilters={setFilters} />
        </div>
        <div className={`grid-col ${!adminActive && selection.count > 0 ? "has-selection-bar" : ""}`}>
          <Breadcrumb
            folderPath={filters.folderPath}
            onNavigate={setFolder}
            rootPath={lockedFolder}
          />
          <AssetGrid
            assets={filtered}
            onSelect={setSelected}
            adminMode={adminActive}
            queued={queued}
            onToggleQueued={toggle}
            selectionEnabled={!adminActive}
            selectedIds={selection.selected}
            onToggleSelect={selection.toggle}
            // In admin mode, shift-range and marquee feed the REMOVAL queue
            // (always additive); otherwise they drive the download selection.
            onRangeSelect={adminActive ? queueRange : selection.selectRange}
            onMarqueeSelect={adminActive ? (ids) => queueMany(ids) : selection.setMany}
          />
        </div>
      </div>
      {!adminActive && (
        <SelectionBar
          count={selection.count}
          filteredCount={filtered.length}
          allSelected={selection.count >= filtered.length && filtered.length > 0}
          onSelectAll={() => selection.setMany(filtered.map((a) => a.id), false)}
          onClear={() => {
            selection.clear();
            setShowBulkDownload(false);
          }}
          onDownload={() => setShowBulkDownload(true)}
          onMagicLink={
            canMagicLink
              ? () => setMagicLinkScope({ kind: "assets", assetIds: [...selection.selected] })
              : undefined
          }
        />
      )}
      {showBulkDownload && selectedAssets.length > 0 && (
        <BulkDownloadModal assets={selectedAssets} onClose={() => setShowBulkDownload(false)} />
      )}
      {selected && (
        <DownloadModal
          asset={selected}
          onClose={() => setSelected(null)}
          onEdit={
            adminActive
              ? (a) => {
                  setSelected(null);
                  setEditingAsset(a);
                }
              : undefined
          }
          onMagicLink={
            canMagicLink
              ? (a) => {
                  // Close the file dialog first — its window-level Escape
                  // handler would otherwise close the wrong (bottom) modal.
                  setSelected(null);
                  setMagicLinkScope({ kind: "assets", assetIds: [a.id] });
                }
              : undefined
          }
        />
      )}
      {editingAsset && adminActive && (
        <EditAssetModal
          asset={editingAsset}
          taxonomy={manifest.taxonomy}
          albumPaths={albumPaths}
          onClose={() => setEditingAsset(null)}
          onUpdated={(a) => addOverlayAsset(a)}
          onRemove={async (a) => {
            // Reuse the existing disposition: move the original to InternalAssets,
            // delete the public variants, add to the blocklist, hide locally.
            const variants = await findVariantsForAsset(a.id);
            await moveAssetToInternal(a.id, variants);
            const current = await fetchBlocklist();
            await uploadBlocklist([...new Set([...current, a.id])]);
            addBlocked([a.id]);
          }}
        />
      )}
      {adminActive && (
        <QueuePanel
          queued={queued}
          assetsById={assetsById}
          onRemove={remove}
          onClear={clear}
          onCommit={() => setShowCommit(true)}
          signedIn={!!auth.account}
        />
      )}
      {showAddAsset && adminActive && (
        <AddAssetModal
          taxonomy={manifest.taxonomy}
          albumPaths={albumPaths}
          onClose={() => setShowAddAsset(false)}
          onAdded={(asset) => addOverlayAsset(asset)}
        />
      )}
      {magicLinkScope && (
        <MagicLinkModal scope={magicLinkScope} onClose={() => setMagicLinkScope(null)} />
      )}
      {showCommit && adminActive && (
        <CommitModal
          assets={queuedAssetsForCommit}
          onClose={() => setShowCommit(false)}
          onComplete={async (succeeded) => {
            // Hide items immediately in local state (the SP Public CDN caches
            // blocklist.json for ~15 min, so a refetch would still see stale).
            addBlocked(succeeded);
            for (const id of succeeded) remove(id);
            // Best-effort: pull the canonical list too so we converge on the
            // SP-side state once cache clears.
            await refetchBlocklist();
          }}
        />
      )}
    </div>
  );
}
